import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { fmtIDR } from "../lib/format";
import { PageHeader, Badge, Card, Btn } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { ChevronLeft, ChevronRight } from "lucide-react";

const TYPE_STYLE = {
  cicilan: { tone: "emerald", label: "Cicilan" },
  invoice: { tone: "amber", label: "Faktur" },
  tugas: { tone: "cyan", label: "Tugas" },
  followup: { tone: "indigo", label: "Follow-up" },
  proyek: { tone: "rose", label: "Proyek" },
  rencana: { tone: "orange", label: "Rencana" },
};

export default function Kalender() {
  const { projectId } = useAuth();
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    const month = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}`;
    const q = `?month=${month}${projectId ? `&project_id=${projectId}` : ""}`;
    api.get(`/calendar/events${q}`).then((r) => setEvents(r.data)).catch(() => {});
  }, [cursor, projectId]);
  useEffect(() => { load(); }, [load]);

  const shift = (d) => setCursor((c) => { const m = c.m + d; return { y: c.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 }; });

  const firstDay = new Date(cursor.y, cursor.m, 1).getDay();
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const monthName = new Date(cursor.y, cursor.m).toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  const evByDay = {};
  events.forEach((e) => {
    const day = parseInt(e.date.slice(8, 10), 10);
    (evByDay[day] = evByDay[day] || []).push(e);
  });
  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="kalender-page">
      <PageHeader title="Kalender" subtitle="Event otomatis: jatuh tempo cicilan/KPR, faktur purchase, tugas, follow-up CRM, timeline proyek" testid="kalender-header">
        <div className="flex items-center gap-2">
          <Btn tone="ghost" onClick={() => shift(-1)} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Btn>
          <span className="text-sm font-medium text-slate-200 min-w-[150px] text-center" data-testid="cal-month-label">{monthName}</span>
          <Btn tone="ghost" onClick={() => shift(1)} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Btn>
        </div>
      </PageHeader>

      <div className="flex gap-2 mb-4 flex-wrap">
        {Object.entries(TYPE_STYLE).map(([k, v]) => <Badge key={k} tone={v.tone}>{v.label}</Badge>)}
      </div>

      <div className="grid grid-cols-7 gap-1 sm:gap-1.5" data-testid="calendar-grid">
        {["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"].map((d) => (
          <div key={d} className="text-center text-[10px] font-mono uppercase tracking-wider text-slate-500 py-1">{d}</div>
        ))}
        {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dateStr = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const dayEvents = evByDay[day] || [];
          const isToday = dateStr === todayStr;
          return (
            <button
              key={day}
              onClick={() => setSelected({ day, dateStr, events: dayEvents })}
              data-testid={`cal-day-${dateStr}`}
              className={`min-h-[72px] sm:min-h-[90px] rounded-lg border p-1.5 text-left transition-colors ${isToday ? "border-emerald-500/60 bg-emerald-500/5" : "border-slate-800 bg-slate-900/60 hover:border-slate-700"}`}
            >
              <div className={`text-xs font-mono ${isToday ? "text-emerald-400 font-semibold" : "text-slate-400"}`}>{day}</div>
              <div className="space-y-0.5 mt-1">
                {dayEvents.slice(0, 3).map((e) => (
                  <div key={e.id} className={`text-[9px] leading-tight truncate rounded px-1 py-0.5 border
                    ${e.type === "cicilan" ? "border-emerald-500/40 text-emerald-300" : e.type === "invoice" ? "border-amber-500/40 text-amber-300" : e.type === "tugas" ? "border-cyan-500/40 text-cyan-300" : e.type === "followup" ? "border-indigo-500/40 text-indigo-300" : "border-rose-500/40 text-rose-300"}`}>
                    {e.title}
                  </div>
                ))}
                {dayEvents.length > 3 && <div className="text-[9px] text-slate-500">+{dayEvents.length - 3} lagi</div>}
              </div>
            </button>
          );
        })}
      </div>

      {selected && (
        <Card className="mt-4" testid="cal-day-detail">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-slate-200">Agenda {selected.dateStr}</h3>
            <button className="text-slate-500 hover:text-white" onClick={() => setSelected(null)} data-testid="cal-detail-close">&times;</button>
          </div>
          {selected.events.length === 0 && <p className="text-slate-500 text-sm">Tidak ada agenda.</p>}
          <div className="space-y-2">
            {selected.events.map((e) => (
              <div key={e.id} className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-lg px-3 py-2.5" data-testid={`cal-event-${e.id}`}>
                <div className="flex items-center gap-2.5">
                  <Badge tone={TYPE_STYLE[e.type]?.tone}>{TYPE_STYLE[e.type]?.label || e.type}</Badge>
                  <span className="text-sm text-slate-200">{e.title}</span>
                </div>
                {e.amount != null && <span className="font-mono text-xs text-emerald-400">{fmtIDR(e.amount)}</span>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
